import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReceivablesLayout from './ReceivablesLayout';
import PlatformButton from '../PlatformButton';
import PlatformInput from '../PlatformInput';
import PlatformText from '../PlatformText';
import LoadingScreen from '../LoadingScreen';
import { useReceivables, RECEIVABLE_ERRORS } from './hooks/useReceivables';
import { useSalespeople } from './hooks/useSalespeople';
import { useSimplifiedSecurity } from '../../hooks/useSimplifiedSecurity';
import { useTranslation } from '../../hooks/useTranslation';
import { normalizePartyNameForDisplay } from '../../utils/platformUtils';
import {
  RECEIVABLE_STATUS,
  daysUntil,
  formatDate,
  formatMoney,
  roundMoney
} from './receivablesUtils';

const FILTERS = {
  ALL: 'all',
  DUE_THIS_WEEK: 'dueThisWeek',
  OVERDUE: 'overdue'
};

const UNASSIGNED_SALESPERSON = '__unassigned__';

const ReceivablesHome = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('receivables');
  const { canManageReceivables, canViewReceivables } = useSimplifiedSecurity();
  const [tab, setTab] = useState('open');
  const [filter, setFilter] = useState(FILTERS.ALL);
  const [salespersonFilter, setSalespersonFilter] = useState('');
  const [search, setSearch] = useState('');
  const { receivables, loading, error } = useReceivables(tab === 'completed' ? 'completed' : 'open');
  const salespeople = useSalespeople();

  const salespersonNames = useMemo(() => {
    const names = {};
    salespeople.forEach((person) => {
      names[person.id] = person.fullName || person.email || person.id;
    });
    return names;
  }, [salespeople]);

  const salespersonOptions = useMemo(() => {
    const byId = new Map();
    salespeople.forEach((person) => {
      byId.set(person.id, person.fullName || person.email || person.id);
    });
    receivables.forEach((item) => {
      if (item.salespersonId && !byId.has(item.salespersonId)) {
        byId.set(item.salespersonId, salespersonNames[item.salespersonId] || item.salespersonId);
      }
    });
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [salespeople, receivables, salespersonNames]);

  const hasUnassignedSales = useMemo(
    () => receivables.some((item) => !item.salespersonId),
    [receivables]
  );

  const matchesSalesperson = (item) => {
    if (!salespersonFilter) return true;
    if (salespersonFilter === UNASSIGNED_SALESPERSON) return !item.salespersonId;
    return item.salespersonId === salespersonFilter;
  };

  const totals = useMemo(() => {
    const scoped = receivables.filter(matchesSalesperson);
    const outstanding = roundMoney(
      scoped.reduce((sum, item) => sum + roundMoney(item.amountOutstanding), 0)
    );
    const overdue = roundMoney(
      scoped.reduce((sum, item) => {
        const days = daysUntil(item.dueDate);
        if (item.status !== RECEIVABLE_STATUS.PAID && days !== null && days < 0) {
          return sum + roundMoney(item.amountOutstanding);
        }
        return sum;
      }, 0)
    );
    return { outstanding, overdue, count: scoped.length };
  }, [receivables, salespersonFilter]);

  const visibleItems = useMemo(() => {
    const term = search.trim().toLowerCase();

    return receivables.filter((item) => {
      if (!matchesSalesperson(item)) return false;

      if (term) {
        const haystack = `${item.customerName || ''} ${item.orderNumber || ''}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }

      if (tab === 'open' && filter === FILTERS.OVERDUE) {
        const days = daysUntil(item.dueDate);
        return days !== null && days < 0;
      }

      if (tab === 'open' && filter === FILTERS.DUE_THIS_WEEK) {
        const days = daysUntil(item.dueDate);
        return days !== null && days >= 0 && days <= 7;
      }

      return true;
    });
  }, [receivables, search, filter, tab, salespersonFilter]);

  const renderDueLabel = (dueDate, status) => {
    if (status === RECEIVABLE_STATUS.PAID) return t('status.paid');
    const days = daysUntil(dueDate);
    if (days === null) return '';
    if (days < 0) return t('status.overdueByDays', { days: Math.abs(days) });
    if (days === 0) return t('status.dueToday');
    return t('status.dueInDays', { days });
  };

  const renderPill = (item) => {
    const days = daysUntil(item.dueDate);
    const isOverdue = item.status !== RECEIVABLE_STATUS.PAID && days !== null && days < 0;
    return (
      <span className={`receivables-pill ${isOverdue ? 'overdue' : item.status}`}>
        {isOverdue ? t('status.overdue') : t(`status.${item.status}`)}
      </span>
    );
  };

  if (!canViewReceivables) {
    return (
      <ReceivablesLayout title={t('home.title')} subtitle={t('home.subtitle')} variant="home">
        <div className="receivables-empty">
          <div className="receivables-empty-icon">🔒</div>
          <PlatformText variant="body">{t('errors.accessDenied')}</PlatformText>
        </div>
      </ReceivablesLayout>
    );
  }

  if (loading) {
    return <LoadingScreen message={t('home.title')} variant="fullscreen" />;
  }

  return (
    <ReceivablesLayout title={t('home.title')} subtitle={t('home.subtitle')} variant="home">
      <div className="receivables-home">
      {error && (
        <div className="receivables-notice">
          {error === RECEIVABLE_ERRORS.PERMISSION_DENIED ? t('errors.permissionDenied') : t('errors.loadFailed')}
        </div>
      )}

      {tab === 'open' && (
        <div className="receivables-summary">
          <div className="receivables-summary-card">
            <div className="receivables-summary-value">{formatMoney(totals.outstanding)}</div>
            <div className="receivables-summary-label">{t('home.totalOutstanding')}</div>
          </div>
          <div className="receivables-summary-card">
            <div className="receivables-summary-value">{totals.count}</div>
            <div className="receivables-summary-label">{t('home.openSales')}</div>
          </div>
          <div className="receivables-summary-card">
            <div className={`receivables-summary-value${totals.overdue > 0 ? ' is-overdue' : ''}`}>
              {formatMoney(totals.overdue)}
            </div>
            <div className="receivables-summary-label">{t('home.overdue')}</div>
          </div>
        </div>
      )}

      {canManageReceivables && (
        <div className="receivables-actions">
          <PlatformButton onClick={() => navigate('/receivables/new')}>
            {t('actions.recordSale')}
          </PlatformButton>
        </div>
      )}

      <div className="receivables-home-panel">
      <div className="receivables-tabs">
        <button
          type="button"
          className={`receivables-tab${tab === 'open' ? ' active' : ''}`}
          onClick={() => setTab('open')}
        >
          {t('tabs.open')}
        </button>
        <button
          type="button"
          className={`receivables-tab${tab === 'completed' ? ' active' : ''}`}
          onClick={() => setTab('completed')}
        >
          {t('tabs.completed')}
        </button>
      </div>

      <div className="receivables-toolbar">
        <div className="receivables-field">
          <PlatformInput
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('filters.searchPlaceholder')}
            aria-label={t('filters.searchPlaceholder')}
          />
        </div>
        <div className="receivables-filter-row">
        {tab === 'open' && (
          <div className="receivables-filters">
            {[FILTERS.ALL, FILTERS.DUE_THIS_WEEK, FILTERS.OVERDUE].map((option) => (
              <button
                key={option}
                type="button"
                className={`receivables-filter-chip${filter === option ? ' active' : ''}`}
                onClick={() => setFilter(option)}
              >
                {t(`filters.${option}`)}
              </button>
            ))}
          </div>
        )}
        {canManageReceivables && (
          <div className="receivables-field receivables-salesperson-filter">
            <select
              className="platform-select"
              value={salespersonFilter}
              onChange={(event) => setSalespersonFilter(event.target.value)}
              aria-label={t('filters.salesperson')}
            >
              <option value="">{t('filters.allSalespeople')}</option>
              {hasUnassignedSales && (
                <option value={UNASSIGNED_SALESPERSON}>{t('filters.unassigned')}</option>
              )}
              {salespersonOptions.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </select>
          </div>
        )}
        </div>
      </div>

      <div className="receivables-list-scroll">
      {visibleItems.length === 0 ? (
        <div className="receivables-empty">
          <div className="receivables-empty-icon">💰</div>
          <PlatformText variant="body">
            {receivables.length === 0
              ? (tab === 'completed' ? t('home.emptyCompleted') : t('home.empty'))
              : t('home.emptyFiltered')}
          </PlatformText>
        </div>
      ) : (
        <div className="receivables-list">
          {visibleItems.map((item) => {
            const days = daysUntil(item.dueDate);
            const isOverdue = item.status !== RECEIVABLE_STATUS.PAID && days !== null && days < 0;
            return (
              <div className="receivables-card" key={item.id}>
                <div className="receivables-card-top">
                  <div>
                    <div className="receivables-card-name">
                      {normalizePartyNameForDisplay(item.customerName) || item.customerName}
                      {item.orderNumber ? (
                        <span className="receivables-card-order">
                          {' '}{t('fields.orderNumber')} #{item.orderNumber}
                        </span>
                      ) : null}
                    </div>
                    <div className={`receivables-card-meta${isOverdue ? ' is-overdue' : ''}`}>
                      {item.dueDate
                        ? `${t('fields.dueDate')}: ${formatDate(item.dueDate)} · ${renderDueLabel(item.dueDate, item.status)}`
                        : ''}
                      {item.salespersonId
                        ? `${item.dueDate ? ' · ' : ''}${salespersonNames[item.salespersonId] || t('fields.salesperson')}`
                        : ''}
                    </div>
                    {item.note ? (
                      <div className="receivables-card-meta">{item.note}</div>
                    ) : null}
                  </div>
                  <div className="receivables-card-amount">
                    {tab === 'completed' ? formatMoney(item.invoiceAmount) : formatMoney(item.amountOutstanding)}
                    <div>{renderPill(item)}</div>
                  </div>
                </div>
                {canManageReceivables && tab === 'open' && (
                  <div className="receivables-card-actions">
                    <PlatformButton onClick={() => navigate(`/receivables/pay/${item.id}`)}>
                      {t('actions.recordPayment')}
                    </PlatformButton>
                    <PlatformButton variant="outline" onClick={() => navigate(`/receivables/edit/${item.id}`)}>
                      {t('actions.edit')}
                    </PlatformButton>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </div>
      </div>
      </div>
    </ReceivablesLayout>
  );
};

export default ReceivablesHome;
